import React, { useCallback, useEffect, useState } from 'react'
import { ProductUpdatesDialog } from './ProductUpdatesDialog'
import { fetchProductUpdates } from '../../services/productUpdatesService'
import type { GetProductUpdatesResult, ProductUpdatesVoteState } from '../../../../types/ProductUpdates'
import { trackEvent } from '../../services/analyticsService'
import { useAppSelector } from '../../hooks'

/**
 * Listens for `openProductUpdates` (same pattern as FeedbackDrawer) and loads payload from Cloud.
 */
export default function ProductUpdatesDialogHost() {
  const isAuthenticated = useAppSelector((s) => !!s.user.userData?.id)
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<GetProductUpdatesResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchProductUpdates()
      setData(res)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not load updates'
      setError(msg)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const handler = () => {
      setOpen(true)
      trackEvent('APP_product_updates_opened', {})
      void load()
    }
    window.addEventListener('openProductUpdates', handler)
    return () => window.removeEventListener('openProductUpdates', handler)
  }, [load])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleVoteStateChange = useCallback((voteState: ProductUpdatesVoteState) => {
    setData((prev) => (prev ? { ...prev, voteState } : null))
  }, [])

  return (
    <ProductUpdatesDialog
      open={open}
      onClose={handleClose}
      data={data}
      loading={loading}
      error={error}
      onRetry={load}
      isAuthenticated={isAuthenticated}
      onVoteStateChange={handleVoteStateChange}
    />
  )
}
